import {
  addHours,
  differenceInMonths,
  format,
  formatDistanceStrict,
  isAfter,
  isSameDay,
  parseISO,
} from 'date-fns';
import enLocale from 'date-fns/locale/en-US';
import esLocale from 'date-fns/locale/es';
import ptLocale from 'date-fns/locale/pt';

import { configValue } from 'utils/config';
import { IDEALIST_TIME_ZONE } from 'utils/constants';

export const LOCALES = {
  en: enLocale,
  es: esLocale,
  pt: ptLocale,
};
export const getDate = (date: string | number | Date) =>
  typeof date === 'string' ? parseISO(date) : date;

const formatDate = (
  date,
  locale,
  // @ts-expect-error ts-migrate(2304) FIXME: Cannot find name 'Intl$DateTimeFormatOptions'.
  params: Intl$DateTimeFormatOptions = Object.freeze({})
) => {
  const { timeZone = undefined, ...spreadParams } = params;
  return new Date(date).toLocaleDateString(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: timeZone || undefined,
    ...spreadParams,
  });
};

const formatTime = (
  date,
  locale,
  // @ts-expect-error ts-migrate(2304) FIXME: Cannot find name 'Intl$DateTimeFormatOptions'.
  params: Intl$DateTimeFormatOptions = Object.freeze({})
) => {
  const { timeZone = undefined, ...spreadParams } = params;
  return new Date(date).toLocaleTimeString(locale, {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: timeZone || undefined,
    ...spreadParams,
  });
};

const SHORTER_DATE_FORMAT = {
  en: 'M/d/yyyy',
  es: 'd/M/yyyy',
  pt: 'd/M/yyyy',
};
export const shorterDate = (date: string | number | Date) =>
  format(getDate(date), SHORTER_DATE_FORMAT[CURRENT_LOCALE]);
const SHORTEST_DATE_FORMAT = {
  en: 'M/d',
  es: 'd/M',
  pt: 'd/M',
};
export const shortestDate = (date: string | number | Date) =>
  format(getDate(date), SHORTEST_DATE_FORMAT[CURRENT_LOCALE]);
const SHORTEST_DATE_WITH_YEAR_FORMAT = {
  en: 'M/d/yy',
  es: 'd/M/yy',
  pt: 'd/M/yy',
};
export const shortestDateWithYear = (date: string | number | Date) =>
  format(getDate(date), SHORTEST_DATE_WITH_YEAR_FORMAT[CURRENT_LOCALE]);
export const shorterTime = (
  date: string | number | Date,
  timezone: string | null | undefined,
  displayTimezone?: boolean
) => {
  // @ts-expect-error ts-migrate(2304) FIXME: Cannot find name 'Intl$DateTimeFormatOptions'.
  const options: Intl$DateTimeFormatOptions = {
    timeZoneName: displayTimezone ? 'short' : undefined,
    timeZone: timezone || undefined,
  };
  return formatTime(date, LOCALES[CURRENT_LOCALE].code, options);
};
const DATETIME_FORMAT = {
  en: 'MM/dd/yyyy  hh:mma',
  es: 'dd/MM/yyyy HH:mm',
  pt: 'dd/MM/yyyy HH:mm',
};
export const dateTime = (date: string | number | Date) =>
  format(getDate(date), DATETIME_FORMAT[CURRENT_LOCALE]);
const SHORT_DATETIME_FORMAT = {
  en: 'M/d/yy h:mma',
  es: 'd/M/yy HH:mm',
  pt: 'd/M/yy HH:mm',
};
export const shortDateTime = (date: string | number | Date) =>
  format(getDate(date), SHORT_DATETIME_FORMAT[CURRENT_LOCALE]);
export const humanDate = (
  date: string | number | Date,
  timeZone?: string | null | undefined,
  opts: {
    shortMonth?: boolean;
  } = {}
) =>
  formatDate(date, LOCALES[CURRENT_LOCALE].code, {
    year: 'numeric',
    month: opts && opts.shortMonth ? 'short' : 'long',
    day: 'numeric',
    hour: undefined,
    minute: undefined,
    timeZone: timeZone || undefined,
  });
const HUMAN_DATETIME_FORMAT = {
  en: 'MMM d, yyyy h:mm a',
  es: 'MMM d, yyyy H:mm',
  pt: 'MMM d, yyyy H:mm',
};
export const humanDateTime = (date: string | number | Date) =>
  format(getDate(date), HUMAN_DATETIME_FORMAT[CURRENT_LOCALE], {
    locale: LOCALES[CURRENT_LOCALE],
  });
export const monthAndYear = (date: string | number | Date) =>
  format(getDate(date), 'MMMM yyyy', {
    locale: LOCALES[CURRENT_LOCALE],
  });
export const dayAndMonth = (date: string | number | Date) =>
  format(getDate(date), 'MMM d', {
    locale: LOCALES[CURRENT_LOCALE],
  });
export const dayNumber = (date: string | number | Date) =>
  format(getDate(date), 'd', {
    locale: LOCALES[CURRENT_LOCALE],
  });
export const shortMonth = (date: string | number | Date) =>
  format(getDate(date), 'MMM', {
    locale: LOCALES[CURRENT_LOCALE],
  }).slice(0, 3);
export const yearOnly = (date: string | number | Date) =>
  format(getDate(date), 'yyyy', {
    locale: LOCALES[CURRENT_LOCALE],
  });
export const shortMonthAndYear = (date: string | number | Date) =>
  format(getDate(date), 'MMM yyyy', {
    locale: LOCALES[CURRENT_LOCALE],
  });
export const now = () => {
  const dtOverride = configValue('idealist', 'dtOverride');

  if (dtOverride) {
    return parseISO(dtOverride);
  }

  return new Date();
};
export const daysInMs = (days: number) => 1000 * 60 * 60 * 24 * days;
export const distanceInWordsToNow = (date: string | number | Date) =>
  formatDistanceStrict(getDate(date), now(), {
    addSuffix: true,
    locale: LOCALES[CURRENT_LOCALE],
    roundingMethod: 'floor',
  });
export const distanceInYearsAndMonthsAbbr = (
  start: string | number | Date,
  end: string | number | Date
) => {
  const distance = differenceInMonths(getDate(start), getDate(end));

  if (!distance) {
    return null;
  }

  const years = Math.floor(distance / 12);
  const months = distance - years * 12;
  return [years, months]
    .map((amt, index) =>
      amt === 0
        ? null
        : `${amt} ${index === 0 ? 'yr' : 'mo'}${amt > 1 ? 's' : ''}`
    )
    .filter((amt) => amt)
    .join(', ');
};

const hasSeconds = (time: string) => time.split(':').length === 3;

export const withSeconds = (time: string) =>
  hasSeconds(time) ? time : `${time}:00`;
// Converts a date or date string to GMT timezone
export const toGMTDate = (date: string | Date) => {
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line no-param-reassign
  date = new Date(date);
  return new Date(date.getTime() + date.getTimezoneOffset() * 60000);
};
export const stripTimezone = (dateString: string) =>
  dateString.replace('Z', '');
// Take a timezone-aware date and return the user's date / time (not timezone-aware)
// as a string
export const formatLocalDate = (d: Date): string => {
  const convertedDate = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return stripTimezone(convertedDate.toISOString());
};
// @TODO: Pass in tz argument, so we can format any date to
// any timezone... or use a library ;)
export const formatIdealistDateTime = (d: Date | string): Date => {
  const options = {
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: IDEALIST_TIME_ZONE,
  };
  const formattedDateParts: Array<{
    type: string;
    value: string;
    // @ts-expect-error ts-migrate(2345) FIXME: Argument of type '{ hour12: boolean; year: string;... Remove this comment to see the full error message
  }> = new Intl.DateTimeFormat('en-us', options).formatToParts(d);
  const dateTimeParams = formattedDateParts
    .filter((datePart) =>
      ['year', 'month', 'day', 'hour', 'minute', 'second'].includes(
        datePart.type
      )
    )
    .map((datePart) => parseInt(datePart.value, 10));
  const [month, day, year, hour, minute, second] = dateTimeParams;
  return new Date(year, month - 1, day, hour, minute, second);
};

const getLocalizedToday = (date: string | Date) => {
  const offsetMatch =
    typeof date === 'string'
      ? date.match(/([+-]?[0-9]+)(?::[0-9]+)?$/)
      : date.getTimezoneOffset();
  const offsetToDate =
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line no-nested-ternary
    typeof offsetMatch === 'number'
      ? offsetMatch
      : offsetMatch
      ? parseInt(offsetMatch.pop(), 10)
      : 0;
  const today = now();
  const offsetFromNow = today.getTimezoneOffset() / 60;
  const offsetTotal = offsetToDate + offsetFromNow;
  return addHours(today, offsetTotal);
};

export const dateIsToday = (date: string | Date) =>
  isSameDay(getLocalizedToday(date), getDate(date));
export const dateIsUpcoming = (date: string | Date) => {
  const today = getLocalizedToday(date);
  return isAfter(getDate(date), today) || isSameDay(today, getDate(date));
};
export const dateIsPast = (date: string | Date) => !dateIsUpcoming(date);
export const getCurrentYear = () => now().getFullYear();
